import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Dropdown from '../../Components/CustomDropdown/customdropdown';
import { useToast } from '../../Utils/toastContext';
import { useSelector } from 'react-redux';
import {
  Activity,
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  FileText,
  RefreshCw,
  ShieldCheck,
  UserRound,
  X,
} from 'lucide-react';
import { formatDateTimeUTC } from '../../Constants/helper-functions';

const ActivityLog = ({ onClose, customerId }) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const loggedInUserRole = useSelector((state) => state.user.roleName);
  const [filter, setFilter] = useState(0);
  const [filterUser, setFilterUser] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [historyData, setHistoryData] = useState([]);
  const [filterUserOptions, setFilterUserOptions] = useState([]);
  const [isUsersLoading, setIsUsersLoading] = useState(true);
  const [isLogsLoading, setIsLogsLoading] = useState(true);
  const [isOverallLoading, setIsOverallLoading] = useState(true);
  const [preSelectedFilter, setpreSelectedFilter] = useState({
    value: 0,
    label: 'All time',
  });
  // const getInitials = (name) => {
  //   if (!name) return '?';

  //   return name
  //     .trim()
  //     .split(/\s+/)
  //     .slice(0, 2)
  //     .map((part) => part.charAt(0).toUpperCase())
  //     .join('');
  // };
  const getInitials = (name) => {
    if (!name) return '?';
    return name.trim().charAt(0).toUpperCase();
  };
  const getActorName = (actor) => {
    return actor?.full_name || 'Unknown User';
  };

  const getActorRole = (actor) => {
    return actor?.roles?.[0]?.name || 'User';
  };

  const formatRole = (role = '') => {
    return role
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // API format:
  // 26/05/2026 10:39:18 AM AEST
  const parseApiDate = (dateString) => {
    if (!dateString) return null;

    const match = dateString.match(
      /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})\s+(AM|PM)/,
    );

    if (!match) return null;

    const [, day, month, year, hour, minute, second, meridiem] = match;

    let hours = parseInt(hour, 10);

    if (meridiem === 'PM' && hours !== 12) {
      hours += 12;
    }

    if (meridiem === 'AM' && hours === 12) {
      hours = 0;
    }

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      hours,
      Number(minute),
      Number(second),
    );
  };

  const formatActivityTime = (dateString) => {
    const date = parseApiDate(dateString);

    if (!date) return dateString || '';

    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatActivityDate = (dateString) => {
    const date = parseApiDate(dateString);

    if (!date) return dateString || '';

    return date.toLocaleDateString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDateGroup = (dateString) => {
    if (!dateString) return 'Unknown Date';

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return 'Unknown Date';
    }

    const today = new Date();

    const isToday =
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();

    if (isToday) {
      return 'Today';
    }

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isYesterday =
      date.getFullYear() === yesterday.getFullYear() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getDate() === yesterday.getDate();

    if (isYesterday) {
      return 'Yesterday';
    }

    return date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getAvatarStyle = (name) => {
    const styles = [
      'bg-[#EEF2FF] text-[#3730A3] ring-1 ring-[#E0E7FF]', // Indigo soft
      'bg-[#F1F5F9] text-[#334155] ring-1 ring-[#E2E8F0]', // Slate
      'bg-[#FDF2F8] text-[#9D174D] ring-1 ring-[#FBCFE8]', // Rose subtle
      'bg-[#F0FDF4] text-[#166534] ring-1 ring-[#BBF7D0]', // Green subtle
      'bg-[#FFFBEB] text-[#92400E] ring-1 ring-[#FDE68A]', // Amber subtle
    ];

    const index = name?.charCodeAt(0) % styles.length;
    return styles[index];
  };
  const [preSelectedFilterUser, setpreSelectedFilterUser] = useState({});
  const navigate = useNavigate();
  // Format date groups
  const groupByDate = (data) => {
    const groups = {};

    data.forEach((item) => {
      const date = getDateGroup(item.created_at);

      if (!groups[date]) {
        groups[date] = [];
      }

      groups[date].push(item);
    });

    return groups;
  };

  const groupedData = groupByDate(historyData);
  const totalActivities = historyData.length;
  const selectedUserLabel = preSelectedFilterUser?.label || 'All Users';

  const getActor = (item) =>
    item?.actor || item?.user || item?.created_by || item?.performed_by || null;

  const getActivityTone = (description = '') => {
    const text = description.toLowerCase();

    if (text.includes('reject') || text.includes('failed')) {
      return {
        icon: AlertTriangle,
        shell: 'bg-red-50 text-red-600 ring-red-100',
        bar: 'from-red-400 to-rose-500',
      };
    }

    if (
      text.includes('approve') ||
      text.includes('complete') ||
      text.includes('verified')
    ) {
      return {
        icon: CheckCircle2,
        shell: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
        bar: 'from-emerald-400 to-teal-500',
      };
    }

    if (text.includes('document') || text.includes('upload')) {
      return {
        icon: FileText,
        shell: 'bg-sky-50 text-sky-600 ring-sky-100',
        bar: 'from-sky-400 to-cyan-500',
      };
    }

    return {
      icon: RefreshCw,
      shell: 'bg-indigo-50 text-[#375AE7] ring-indigo-100',
      bar: 'from-[#375AE7] to-violet-500',
    };
  };

  const handleFilterChange = (selectedOption) => {
    console.log('Selected Option:', selectedOption);
    setFilter(selectedOption.value);
    setpreSelectedFilter(selectedOption);
    const today = new Date();
    let startDate = new Date();

    if (selectedOption.value === 7) {
      // Last 7 days
      startDate.setDate(today.getDate() - 7);
    } else if (selectedOption.value === 30) {
      // Last 30 days
      startDate.setDate(today.getDate() - 30);
    }

    // Format as YYYY-MM-DD (or as your API expects)
    const formattedStart = startDate.toISOString().split('T')[0];
    const formattedEnd = today.toISOString().split('T')[0];

    setStartDate(formattedStart);
    setEndDate(formattedEnd);
  };
  const handleFilterChangeUser = (selectedOption) => {
    console.log('Selected Option:', selectedOption);
    setFilterUser(selectedOption.value);
    setpreSelectedFilterUser(selectedOption);
  };
  const filterOptions = [
    { value: 0, label: 'All time' },
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
  ];
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const getActivityLogs = async () => {
    try {
      let response = '';
      let baseUrl = `/api/v1/audit-logs?limit=100&page=1&user_id=${customerId}&resource=customer_onboarding&resource=stakeholder_onboarding&resource=customer_agency_link`;

      // Add user_id only if it's valid
      if (filterUser) {
        baseUrl += `&user_id=${filterUser}`;
      }

      // Add date filters only if filter is not 0
      if (filter !== 0) {
        baseUrl += `&start_date=${startDate}&end_date=${endDate}`;
      }
      response = await GetAPIcalls(baseUrl);

      if (response.status == 200) {
        setHistoryData(response.data.items);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      setHistoryData([]);
      showToast(err.message, 'error');
    } finally {
      setTimeout(function () {}, 100);
    }
  };
  const getUsers = async () => {
    try {
      let response = '';
      response = await GetAPIcalls(`/api/v1/agency/customers`);

      if (response.status == 200) {
        const users = [];
        users.push({
          value: null,
          label: 'All Users',
        });
        response.data.customers.forEach((profile) => {
          users.push({
            value: profile.user_id,
            label: profile.first_name + ' ' + profile.last_name,
          });
        });
        setFilterUserOptions(users);
        setpreSelectedFilterUser(users[0]);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      setFilterUserOptions([]);
      showToast(err.message, 'error');
    } finally {
      setTimeout(function () {}, 100);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setIsUsersLoading(true);
      await getUsers();
      setIsUsersLoading(false);
    };

    const fetchLogs = async () => {
      setIsLogsLoading(true);
      await getActivityLogs();
      setIsLogsLoading(false);
    };

    fetchUsers();
    fetchLogs();
  }, [customerId]);

  useEffect(() => {
    // Recalculate overall loading whenever one of them changes
    setIsOverallLoading(isUsersLoading || isLogsLoading);
  }, [isUsersLoading, isLogsLoading]);

  useEffect(() => {
    // When filters change, only re-fetch activity logs, not users
    const fetchFilteredLogs = async () => {
      setIsLogsLoading(true);
      await getActivityLogs();
      setIsLogsLoading(false);
    };
    fetchFilteredLogs();
  }, [loggedInUserRole, filterUser, filter, startDate, endDate]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 px-3 py-6 backdrop-blur-sm">
      <div className="relative flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white bg-white shadow-[0_24px_70px_rgba(15,23,42,0.18)]">
        <div className="relative overflow-hidden border-b border-blue-100 bg-gradient-to-br from-[#F8FBFF] via-[#EEF4FF] to-[#F5F0FF] px-5 py-5 text-slate-900 md:px-7">
          <div className="absolute inset-x-0 bottom-0 h-px bg-white/70" />
          <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-[#375AE7] shadow-sm ring-1 ring-blue-100">
                <Activity size={22} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#375AE7]">
                  Audit Trail
                </p>
                <h2 className="mt-1 text-2xl font-semibold leading-tight">
                  Activity Log
                </h2>
                <p className="mt-1 max-w-xl text-sm leading-5 text-slate-600">
                  Review every user action, status change, and document update
                  in one clear timeline.
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-600 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 hover:text-slate-900"
              aria-label="Close activity log"
            >
              <X size={18} />
            </button>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-white/80 px-4 py-3 shadow-sm ring-1 ring-blue-100">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                Events
              </p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">
                {totalActivities}
              </p>
            </div>
            <div className="rounded-lg bg-white/80 px-4 py-3 shadow-sm ring-1 ring-blue-100">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                User
              </p>
              <p className="mt-1 truncate text-sm font-semibold text-slate-900">
                {selectedUserLabel}
              </p>
            </div>
            <div className="rounded-lg bg-white/80 px-4 py-3 shadow-sm ring-1 ring-blue-100">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                Time
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {preSelectedFilter?.label || 'All time'}
              </p>
            </div>
          </div>
        </div>

        <div className="border-b border-slate-200 bg-slate-50/80 px-5 py-4 md:px-7">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
              <ShieldCheck size={18} className="text-[#375AE7]" />
              <span>Secure</span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 md:w-[420px]">
              {!isUsersLoading && filterUserOptions.length > 0 && (
                <div>
                  <Dropdown
                    label=""
                    options={filterUserOptions}
                    preselectedValue={preSelectedFilterUser}
                    search={true}
                    onSelect={handleFilterChangeUser}
                    parentClass="border-slate-200 shadow-sm hover:border-[#375AE7]/50"
                  />
                </div>
              )}
              <div>
                <Dropdown
                  label=""
                  options={filterOptions}
                  preselectedValue={preSelectedFilter}
                  onSelect={handleFilterChange}
                  parentClass="border-slate-200 shadow-sm hover:border-[#375AE7]/50"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-white px-5 py-5 md:px-7">
          {isOverallLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-slate-100 bg-slate-50/80 p-4"
                >
                  <div className="flex animate-pulse gap-4">
                    <div className="h-11 w-11 rounded-full bg-slate-200" />
                    <div className="flex-1 space-y-3">
                      <div className="h-3 w-2/3 rounded bg-slate-200" />
                      <div className="h-3 w-1/3 rounded bg-slate-200" />
                      <div className="h-8 w-full rounded bg-slate-100" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : Object.keys(groupedData).length === 0 ? (
            <div className="flex h-full min-h-[320px] flex-col items-center justify-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <CalendarDays size={28} />
              </div>
              <p className="mt-4 text-base font-semibold text-slate-800">
                No activity found
              </p>
              <p className="mt-1 max-w-sm text-sm text-slate-500">
                Try a different user or date range to view audit events.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedData).map(([date, items]) => (
                <section key={date} className="relative">
                  <div className="sticky top-0 z-20 mb-4 flex items-center gap-3 bg-white/95 py-1 backdrop-blur">
                    <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
                      <CalendarDays size={14} className="text-[#375AE7]" />
                      {date}
                    </span>
                    <div className="h-px flex-1 bg-slate-200" />
                    <span className="text-xs font-medium text-slate-400">
                      {items.length} {items.length === 1 ? 'event' : 'events'}
                    </span>
                  </div>

                  <div className="space-y-3">
                    {items.map((item, index) => {
                      const actor = getActor(item);
                      const actorName = getActorName(actor);
                      const tone = getActivityTone(item.description);
                      const Icon = tone.icon;

                      return (
                        <div
                          key={item.id || `${date}-${index}`}
                          className="group relative rounded-lg border border-slate-100 bg-white p-4 shadow-[0_8px_26px_rgba(15,23,42,0.05)] transition hover:-translate-y-0.5 hover:border-[#375AE7]/25 hover:shadow-[0_16px_36px_rgba(55,90,231,0.12)]"
                        >
                          <div
                            className={`absolute inset-y-3 left-0 w-1 rounded-r-full bg-gradient-to-b ${tone.bar}`}
                          />
                          <div className="flex gap-4 pl-2">
                            <div className="relative shrink-0">
                              <div
                                className={`flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold ${getAvatarStyle(
                                  actorName,
                                )}`}
                              >
                                {actorName === 'Unknown User' ? (
                                  <UserRound size={18} />
                                ) : (
                                  getInitials(actorName)
                                )}
                              </div>
                              <div
                                className={`absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white ${tone.shell}`}
                              >
                                <Icon size={13} />
                              </div>
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900">
                                    {item.description || 'Activity performed'}
                                  </p>
                                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 font-medium ring-1 ring-slate-200">
                                      <UserRound size={13} />
                                      {actorName}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 font-medium ring-1 ring-slate-200">
                                      <ShieldCheck size={13} />
                                      {formatRole(getActorRole(actor))}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex shrink-0 items-center gap-2 rounded-full bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                                  <Clock3
                                    size={14}
                                    className="text-[#375AE7]"
                                  />
                                  {formatDateTimeUTC(item.created_at)}
                                </div>
                              </div>

                              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                                <span className="text-xs font-medium text-slate-400">
                                  {formatDateTimeUTC(item.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end border-t border-slate-200 bg-slate-50 px-5 py-4 md:px-7">
          <button
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-lg bg-[#111827] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1F2937]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
